//ICS-202
import React, { useCallback } from 'react';
import IncidentObjectivesTable from '../../IncidentObjectivesTable';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from '../../hooks/incidentInfoHooks';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import {
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import { OperationalPeriodScheduleControl } from './OperationalPeriodScheduleControl';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

import Fema202Card from 'components/IAP/femaCards/Fema202Card';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import {
  Ics202DraftButton,
  Ics202DraftFlow,
  Ics202DraftTarget,
  Ics202GeneratingBody,
  Ics202SectionSparkle,
} from 'components/IAP/femaCards/Ics202DraftFlow';
import { USCG_ICS202_DRAFT_SOURCES } from 'components/IAP/femaCards/ics202DraftPrompt';
import { getIncidentCardTitle } from '../USCGPage';

import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { Field, FieldLabel } from '@/components/ui/field';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { Switch } from '@/components/ui/switch';

import { ICS204_TABLE } from './AssignmentList/assignmentCardStyles';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from './icsBoxPrimitives';
import {
  COMMUNITY_LIFELINES,
  ICS202_BOX_LABELS,
  ICS202_INCIDENT_LOCATION_HINT,
  ICS202_INCIDENT_NAME_HINT,
  ICS202_OPERATIONAL_PERIOD_HINT,
} from './ics202BoxLabels';
import Ics202SignatureBox from './Ics202SignatureBox';
import SaveableField from './SaveableField';

const communityLifelinesOptions = COMMUNITY_LIFELINES.map((lifeline) => ({
  value: lifeline,
  label: lifeline,
}));

const IncidentObjectives: React.FC = () => {
  const { data: incidentInfo } = useIncidentInfo();
  const incidentMutation = useUpdateIcsInfo();
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();

  const incident = useIncident();
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  // The FEMA 202 prints a situational awareness box the Coast Guard 202 has no
  // equivalent for, so the editor only appears where the field is published.
  const formFamily = useIcsFormFamily();

  const { effectivePeriodNumber, readOnlyForms, isFuture } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const recordVersion = useRecordFormVersionSave(
    'ICS_202',
    effectivePeriodNumber
  );

  /**
   * A box's save, recorded as a version once the write has landed.
   *
   * Every commit sends the whole record with one box laid over it, so the
   * columns the user did not touch keep what they already held, and the
   * snapshot the server then takes is of values that are already persisted.
   */
  const saveIncidentInfo = useCallback(
    (patch: Record<string, unknown>) => {
      incidentMutation.mutate(
        { ...incidentInfo, ...patch } as Parameters<
          typeof incidentMutation.mutate
        >[0],
        {
          onSuccess: () => {
            if (!readOnlyForms) recordVersion.mutate();
          },
        }
      );
    },
    [incidentInfo, incidentMutation, readOnlyForms, recordVersion]
  );

  const communityLifelinesValue = incidentInfo?.communityLifelines ?? [];

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: 'ICS_202_CG',
      data: {},
      mapSketch: null,
      format,
    });
  };

  const exportActions = (
    <span data-tour="ics202-export">
      <FormExportButton
        formname="ICS_202_CG"
        onExport={handleExport}
        label="Preview ICS-202"
        disabled={isExporting}
      />
    </span>
  );

  const uscgActions = (
    <div className="tw:flex tw:items-center tw:gap-2">
      <Ics202DraftButton
        label="Generate"
        sources={[...USCG_ICS202_DRAFT_SOURCES]}
      />
      {exportActions}
    </div>
  );

  const communityLifelinesControl = (
    <MultiIdCombobox
      options={communityLifelinesOptions}
      value={communityLifelinesValue}
      placeholder="Click to select community lifelines..."
      onChange={(selected) => {
        if (!canEditCards) return;
        incidentMutation.mutate({
          ...incidentInfo,
          communityLifelines: selected,
        });
      }}
    />
  );

  // The FEMA 202 prints a different set of boxes in a different order, so it
  // renders from the descriptor schema rather than from this card's own layout.
  if (formFamily === 'fema') {
    return (
      <Ics202DraftFlow disabled={!canEditCards}>
        <Fema202Card
          disabled={!canEditCards}
          headerActions={
            <div className="tw:flex tw:items-center tw:gap-2">
              <Ics202DraftButton />
              {exportActions}
            </div>
          }
          legacyExtras={
            <Field className="tw:gap-2" data-section="communityLifelines">
              <FieldLabel htmlFor="communityLifelines">
                Community Lifelines
              </FieldLabel>
              {communityLifelinesControl}
            </Field>
          }
        />
      </Ics202DraftFlow>
    );
  }

  // Boxes of the printed ICS 202-CG, in printed order. Boxes 10 and 14 are the
  // same signature reprinted on page 3, so they close the card as one box.
  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_202" />

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.incidentName}
          testId="ics202-section-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident name">
              {ICS202_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics202-field-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.incidentLocation}
          testId="ics202-section-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident location">
              {ICS202_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics202-field-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.operationalPeriod}
          testId="ics202-section-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the planned next operational period">
              {ICS202_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics202-field-operational-period" />
        </Ics204CardSection>
      </div>

      <Ics202DraftTarget source="communityLifelines">
        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.communityLifelines}
          testId="ics202-section-community-lifelines"
          className="tw:mt-0"
          headerAction={
            <Ics202SectionSparkle
              source="communityLifelines"
              label={ICS202_BOX_LABELS.communityLifelines}
            />
          }
        >
          <Ics202GeneratingBody source="communityLifelines">
            <Field data-section="communityLifelines">
              {communityLifelinesControl}
            </Field>
          </Ics202GeneratingBody>
        </Ics204CardSection>
      </Ics202DraftTarget>

      <Ics202DraftTarget source="priorities">
        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.priorities}
          testId="ics202-section-priorities"
          className="tw:mt-0"
          headerAction={
            <Ics202SectionSparkle
              source="priorities"
              label={ICS202_BOX_LABELS.priorities}
            />
          }
        >
          <Ics202GeneratingBody source="priorities">
            <SaveableField
              id="priorities"
              label="Incident Priorities"
              hideLabel
              placeholder="Enter incident priorities..."
              multiline
              disabled={!canEditCards}
              value={incidentInfo?.priorities}
              onSave={(value) => saveIncidentInfo({ priorities: value })}
            />
          </Ics202GeneratingBody>
        </Ics204CardSection>
      </Ics202DraftTarget>

      <Ics202DraftTarget source="objectives">
        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.objectives}
          testId="ics202-section-objectives"
          className="tw:mt-0"
          headerAction={
            <Ics202SectionSparkle
              source="objectives"
              label={ICS202_BOX_LABELS.objectives}
            />
          }
        >
          <Ics202GeneratingBody source="objectives">
            <div data-section="objectives">
              <IncidentObjectivesTable
                readOnly={!canEditCards}
                className={ICS204_TABLE}
              />
            </div>
          </Ics202GeneratingBody>
        </Ics204CardSection>
      </Ics202DraftTarget>

      <Ics202DraftTarget source="operationalPeriodCommandEmphasis">
        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.commandEmphasis}
          testId="ics202-section-command-emphasis"
          className="tw:mt-0"
          headerAction={
            <Ics202SectionSparkle
              source="operationalPeriodCommandEmphasis"
              label={ICS202_BOX_LABELS.commandEmphasis}
            />
          }
        >
          <Ics202GeneratingBody source="operationalPeriodCommandEmphasis">
            <SaveableField
              id="operationalPeriodCommandEmphasis"
              label="Operational Period Command Emphasis"
              hideLabel
              placeholder="Enter operational period command emphasis..."
              multiline
              disabled={!canEditCards}
              value={incidentInfo?.operationalPeriodCommandEmphasis}
              onSave={(value) =>
                saveIncidentInfo({ operationalPeriodCommandEmphasis: value })
              }
            />
          </Ics202GeneratingBody>
        </Ics204CardSection>
      </Ics202DraftTarget>

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
        <Ics202DraftTarget
          source="siteSafetyPlan"
          className="tw:h-full tw:min-w-0"
        >
          <Ics204CardSection
            boxLabel={ICS202_BOX_LABELS.siteSafetyPlanRequired}
            testId="ics202-section-site-safety-plan-required"
            className="tw:mt-0 tw:h-full tw:min-w-0"
            headerAction={
              <Ics202SectionSparkle
                source="siteSafetyPlan"
                label={ICS202_BOX_LABELS.siteSafetyPlanRequired}
              />
            }
          >
            <Ics202GeneratingBody source="siteSafetyPlan">
              <Field orientation="horizontal" className="tw:gap-3">
                <Switch
                  id="siteSafetyPlan"
                  disabled={!canEditCards}
                  checked={!!incidentInfo?.siteSafetyPlan}
                  onCheckedChange={(checked) =>
                    saveIncidentInfo({ siteSafetyPlan: checked })
                  }
                />
                {/* The paper form is a Yes/No pair of checkboxes, so the switch
                states its answer rather than repeating the box title. */}
                <FieldLabel htmlFor="siteSafetyPlan" className="tw:font-normal">
                  {incidentInfo?.siteSafetyPlan ? 'Yes' : 'No'}
                </FieldLabel>
              </Field>
            </Ics202GeneratingBody>
          </Ics204CardSection>
        </Ics202DraftTarget>

        <Ics202DraftTarget
          source="siteSafetyPlanLocation"
          className="tw:h-full tw:min-w-0"
        >
          <Ics204CardSection
            boxLabel={ICS202_BOX_LABELS.siteSafetyPlanLocation}
            testId="ics202-section-site-safety-plan-location"
            className="tw:mt-0 tw:h-full tw:min-w-0"
            headerAction={
              <Ics202SectionSparkle
                source="siteSafetyPlanLocation"
                label={ICS202_BOX_LABELS.siteSafetyPlanLocation}
              />
            }
          >
            <Ics202GeneratingBody source="siteSafetyPlanLocation">
              <SaveableField
                id="siteSafetyPlanLocation"
                label="Site Safety Plan located at"
                hideLabel
                placeholder="Enter site safety plan location..."
                disabled={!canEditCards}
                value={incidentInfo?.siteSafetyPlanLocation}
                onSave={(value) =>
                  saveIncidentInfo({ siteSafetyPlanLocation: value })
                }
              />
            </Ics202GeneratingBody>
          </Ics204CardSection>
        </Ics202DraftTarget>
      </div>

      <Ics202DraftTarget source="criticalInformationRequirements">
        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.criticalInformationRequirements}
          testId="ics202-section-critical-information-requirements"
          className="tw:mt-0"
          headerAction={
            <Ics202SectionSparkle
              source="criticalInformationRequirements"
              label={ICS202_BOX_LABELS.criticalInformationRequirements}
            />
          }
        >
          <Ics202GeneratingBody source="criticalInformationRequirements">
            <SaveableField
              id="criticalInformationRequirements"
              label="Critical Information Requirements"
              hideLabel
              placeholder="Enter critical information requirements..."
              multiline
              disabled={!canEditCards}
              value={incidentInfo?.criticalInformationRequirements}
              onSave={(value) =>
                saveIncidentInfo({ criticalInformationRequirements: value })
              }
            />
          </Ics202GeneratingBody>
        </Ics204CardSection>
      </Ics202DraftTarget>

      <Ics202DraftTarget source="limitationsAndConstraints">
        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.limitationsAndConstraints}
          testId="ics202-section-limitations-and-constraints"
          className="tw:mt-0"
          headerAction={
            <Ics202SectionSparkle
              source="limitationsAndConstraints"
              label={ICS202_BOX_LABELS.limitationsAndConstraints}
            />
          }
        >
          <Ics202GeneratingBody source="limitationsAndConstraints">
            <SaveableField
              id="limitationsAndConstraints"
              label="Limitations and Constraints"
              hideLabel
              placeholder="Enter limitations and constraints..."
              multiline
              disabled={!canEditCards}
              value={incidentInfo?.limitationsAndConstraints}
              onSave={(value) =>
                saveIncidentInfo({ limitationsAndConstraints: value })
              }
            />
          </Ics202GeneratingBody>
        </Ics204CardSection>
      </Ics202DraftTarget>

      <Ics202DraftTarget source="keyDecisionsAndProcedures">
        <Ics204CardSection
          boxLabel={ICS202_BOX_LABELS.keyDecisionsAndProcedures}
          testId="ics202-section-key-decisions-and-procedures"
          className="tw:mt-0"
          headerAction={
            <Ics202SectionSparkle
              source="keyDecisionsAndProcedures"
              label={ICS202_BOX_LABELS.keyDecisionsAndProcedures}
            />
          }
        >
          <Ics202GeneratingBody source="keyDecisionsAndProcedures">
            <SaveableField
              id="keyDecisionsAndProcedures"
              label="Key Decisions and Procedures"
              hideLabel
              placeholder="Enter key decisions and procedures..."
              multiline
              disabled={!canEditCards}
              value={incidentInfo?.keyDecisionsAndProcedures}
              onSave={(value) =>
                saveIncidentInfo({ keyDecisionsAndProcedures: value })
              }
            />
          </Ics202GeneratingBody>
        </Ics204CardSection>
      </Ics202DraftTarget>

      <Ics202SignatureBox periodNumber={effectivePeriodNumber} />
    </div>
  );

  // With the map open the planning rail is a narrow column beside it, so the
  // future-period cards drop their chrome and render as bare content. Dark mode
  // is class-scoped with no global provider, so it has to be carried here too.
  if (isMapVisible && isFuture) {
    return (
      <Ics202DraftFlow disabled={!canEditCards}>
        <div
          className="dark content tw:text-foreground mt-2"
          data-tour="ics202-card"
        >
          {body}
        </div>
      </Ics202DraftFlow>
    );
  }

  return (
    <Ics202DraftFlow disabled={!canEditCards}>
      <Card
        data-testid="uscg-202-card"
        data-tour="ics202-card"
        data-form-family="uscg"
        className="dark tw:bg-background tw:text-foreground"
      >
        <CardHeader>
          <CardTitle>{getIncidentCardTitle('incidentObjectives')}</CardTitle>
          <CardAction>{uscgActions}</CardAction>
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
    </Ics202DraftFlow>
  );
};

export default IncidentObjectives;
